import React from 'react';
import { mutate } from 'swr';
import { observer } from 'mobx-react';
import { useTheme } from 'styled-components';

import { LargeText } from '@ui-library/typography';
import { Flex } from '@ui-library/flex';
import { Form } from '@ui-library/form';
import { Button } from '@ui-library/button';
import { useConfirmModal } from '@hooks/useModalView';
import useStores from '@stores/useStores';
import { ContextMenuV2 } from '@components/common/contextMenu';
import { useOAuth } from '@components/common/accountManagement/hooks/useOAuth';
import { canCreateDestinationAccount } from '@hooks/useActionGuard/utils/permissionChecks';
import { DestinationFeatureFlags, experiment } from '@lib/experiment';
import { ApiGetResource } from '@api';

import {
  getResourceCacheKey,
  useLoadAccountsIntoAccountManagementStore,
} from './hooks/useLoadAccountsIntoAccountManagementStore';
import { AccountManagementCtx, AccountManagementStore, useAccountManagementStore } from './store';
import { AccountDetails } from './common/accountDetails';
import { CreateAccountModal } from './common/createAccountMultiStepModal';
import { SelectAccountModal } from './common/selectAccountMultiStepModal';
import { AccountManagementBase } from './common/accountManagementBase';
import { DestinationAuthSection } from './legacy/destOAuthSection';

const RE_AUTH_WARNING_MESSAGE =
  "Please be aware that scopes, access, and other settings made during re-authorization will affect this account's linked Destinations.";

const mapVariantToSupportedAccountTypes = {
  delivery: 'rudderAccountId',
  regulation: 'rudderDeleteAccountId',
} as const;

interface AccountManagementV1Props {
  initialAccountId?: string;
  accountId: string | undefined;
  disabled?: boolean;
  onComplete: (accountId: string) => void;
  onAccountReauthorize?: (accountId: string) => void;
  resourceDefinitionName: string;
}

// ts-prune-ignore-next
export const AccountManagementV1: React.FC<AccountManagementV1Props> = observer(
  ({
    accountId = '',
    initialAccountId,
    disabled,
    onComplete,
    onAccountReauthorize,
    resourceDefinitionName,
  }) => {
    const { workspaceStore } = useStores();
    const store = useAccountManagementStore();
    const { setReactionDisposer, compatibleAccounts } = store;
    setReactionDisposer((accountId) => {
      onComplete(accountId);
      setShowCreateAccountModal(false);
      setShowSelectAccountModal(false);
    });

    const selectedAccountElement = <AccountDetails store={store} accountId={accountId} />;

    const [showCreateAccountModal, setShowCreateAccountModal] = React.useState(false);
    const [showSelectAccountModal, setShowSelectAccountModal] = React.useState(false);

    const { startReOAuth, loading } = useOAuth({
      onComplete: (accountId: string) => {
        mutate(
          getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
        ).finally(() => {
          store.setAccountId(accountId);
          onAccountReauthorize?.(accountId);
          showReauthorizeAccountModal(false);
        });
      },
    });

    const theme = useTheme();

    const [reauthorizeAccountModal, showReauthorizeAccountModal] = useConfirmModal({
      title: 'Are you sure you want to re-authorize?',
      content: (
        <LargeText color={theme.token.colorTextDescription}>{RE_AUTH_WARNING_MESSAGE}</LargeText>
      ),
      saveLabel: 'Re-authorize account',
      saveLabelBtnType: { type: 'primary', variant: 'danger' },
      closeOnSave: false,
      loading,
      onSave: () => startReOAuth({ accountId }),
      onClose: () => showReauthorizeAccountModal(false),
    });

    const resetStoreAndShowModal = (showModal: (flag: boolean) => void) => {
      store.reset();
      showModal(true);
    };

    const createDestinationAccountPermission = canCreateDestinationAccount();

    let actionElement = (
      <Button
        size="middle"
        onClick={() => resetStoreAndShowModal(setShowCreateAccountModal)}
        name="Create account"
        disabled={!createDestinationAccountPermission.isAllowed}
        tooltip={{ title: createDestinationAccountPermission.denialReason }}
      />
    );

    if (accountId) {
      actionElement = (
        <Button
          size="middle"
          onClick={() => resetStoreAndShowModal(setShowSelectAccountModal)}
          name="Switch"
        />
      );

      if (store.isAccountOAuth(accountId)) {
        actionElement = (
          <ContextMenuV2
            items={[
              {
                key: 'switch-account',
                label: 'Switch account',
                onClick: () => resetStoreAndShowModal(setShowSelectAccountModal),
              },
              {
                key: 'reauthorize-account',
                label: 'Re-authorize account',
                onClick: () => showReauthorizeAccountModal(true),
              },
            ]}
            size="small"
          />
        );
      }
    } else if (compatibleAccounts.length > 0) {
      actionElement = (
        <Button
          size="middle"
          onClick={() => resetStoreAndShowModal(setShowSelectAccountModal)}
          name="Select"
        />
      );
    }

    return (
      <>
        <CreateAccountModal
          open={showCreateAccountModal}
          onClose={() => setShowCreateAccountModal(false)}
        />
        <SelectAccountModal
          open={showSelectAccountModal}
          onClose={() => setShowSelectAccountModal(false)}
          selectedAccountId={accountId}
          initialAccountId={initialAccountId}
        />
        {reauthorizeAccountModal}
        <Flex justifyContent="space-between" alignItemsCenter flexGrow>
          {selectedAccountElement}
          {!disabled && actionElement}
        </Flex>
      </>
    );
  },
);

type DestinationAccountManagementProps = AccountManagementV1Props & {
  title?: string;
  variant?: 'regulation' | 'delivery';
};

const DestinationAccountManagement: React.FC<DestinationAccountManagementProps> = observer(
  ({
    resourceDefinitionName,
    title,
    accountId = '',
    initialAccountId,
    disabled,
    onComplete,
    onAccountReauthorize,
    variant = 'delivery',
  }) => {
    const [form] = Form.useForm();
    const { destinationDefsListStore } = useStores();

    const isNewAccountManagementEnabled = experiment.isDestinationFeatureEnabled(
      resourceDefinitionName,
      DestinationFeatureFlags.enableNewAccountManagementByDestination,
    );

    const destinationDefinition =
      destinationDefsListStore.getDestinationDefByName(resourceDefinitionName);

    const supportedAccountTypes =
      destinationDefinition?.config?.supportedAccountDefinitions?.[
        mapVariantToSupportedAccountTypes[variant]
      ] || [];

    const destinationTypeSupportsAccountDefinitions = supportedAccountTypes.length > 0;
    const accountManagementStore = React.useMemo(
      () =>
        new AccountManagementStore(form, {
          resourceType: 'destination',
          resourceDefinitionName,
          supportedAccountTypes,
        }),
      [],
    );

    const { isLoading } = useLoadAccountsIntoAccountManagementStore({
      accountManagementStore,
      legacyAccountsRole:
        destinationDefinition!.config?.auth?.role || destinationDefinition!.name.toLowerCase(),
    });

    const newAccountManagementComponent = (
      <AccountManagementV1
        accountId={accountId || initialAccountId}
        initialAccountId={initialAccountId}
        disabled={disabled}
        onComplete={onComplete}
        onAccountReauthorize={onAccountReauthorize}
        resourceDefinitionName={resourceDefinitionName}
      />
    );

    const legacyAccountManagementComponent = (
      <DestinationAuthSection
        destinationDef={destinationDefinition!}
        initiallySelectedAccId={initialAccountId || ''}
        isDisabled={!!disabled}
        updateAccount={onComplete}
      />
    );

    const content = (() => {
      // Only check if a workspace has been migrated for those destination types
      // that support account definitions and have account management feature flag enabled
      if (isNewAccountManagementEnabled && destinationTypeSupportsAccountDefinitions) {
        // A workspace has been migrated IFF the new destination accounts list
        // endpoint is empty AND the legacy list accounts is NON empty
        if (
          accountManagementStore.compatibleAccounts.length === 0 &&
          accountManagementStore.legacyAccounts.length > 0
        ) {
          return legacyAccountManagementComponent;
        }

        return (
          <AccountManagementCtx.Provider value={accountManagementStore}>
            {newAccountManagementComponent}
          </AccountManagementCtx.Provider>
        );
      }

      return legacyAccountManagementComponent;
    })();

    return (
      <AccountManagementBase loading={isLoading} title={title}>
        {content}
      </AccountManagementBase>
    );
  },
);

type SourceAccountManagementProps = AccountManagementV1Props & {
  title?: string;
};

const SourceAccountManagement: React.FC<SourceAccountManagementProps> = observer(
  ({
    resourceDefinitionName,
    title,
    accountId = '',
    initialAccountId,
    disabled,
    onComplete,
    onAccountReauthorize,
  }) => {
    const [form] = Form.useForm();
    const { sourceDefinitionsListStore } = useStores();

    const sourceDefinition = sourceDefinitionsListStore.getByName(resourceDefinitionName);

    const supportedAccountTypes =
      sourceDefinition?.config?.supportedAccountDefinitions?.rudderAccountId || [];

    const accountManagementStore = React.useMemo(
      () =>
        new AccountManagementStore(form, {
          resourceType: 'source',
          resourceDefinitionName,
          supportedAccountTypes,
        }),
      [],
    );

    const { isLoading } = useLoadAccountsIntoAccountManagementStore({
      accountManagementStore,
    });

    if (supportedAccountTypes.length === 0) {
      return null;
    }

    return (
      <AccountManagementBase loading={isLoading} title={title}>
        <AccountManagementCtx.Provider value={accountManagementStore}>
          <AccountManagementV1
            accountId={accountId || initialAccountId}
            initialAccountId={initialAccountId}
            disabled={disabled}
            onComplete={onComplete}
            onAccountReauthorize={onAccountReauthorize}
            resourceDefinitionName={resourceDefinitionName}
          />
        </AccountManagementCtx.Provider>
      </AccountManagementBase>
    );
  },
);

type AccountManagementProps =
  | (DestinationAccountManagementProps & { resourceType: 'destination' })
  | (SourceAccountManagementProps & { resourceType: 'source' });

export const AccountManagement: React.FC<AccountManagementProps> = observer(
  ({ resourceType, ...props }) => {
    if (resourceType === 'destination') {
      return <DestinationAccountManagement {...props} />;
    }

    return <SourceAccountManagement {...props} />;
  },
);
